import { all, put, select, call, debounce } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import qs from 'qs';

import { shapeValidationActions as actions } from '.';
import {
  RingValidationPayload,
  ShapeValidationResponse,
  RingValidationInput,
  DiscValidationPayload,
  DiscValidationInput,
  FrameValidationInput,
  FrameValidationPayload,
  StripValidationInput,
  StripValidationPayload,
  ShapeValidation,
} from './types';
import { transformValidationDTO } from '../utils';
import { selectSingleItem } from '../../singleItem/slice/selectors';
import { SingleItemState } from '../../singleItem/slice/types';

const shapeValidationMemo: { [k: string]: ShapeValidation } = {};

const setInMemo = (id: string, value: ShapeValidation) => {
  shapeValidationMemo[id] = value;
  return shapeValidationMemo[id];
};

const getFromMemo = (id: string) => {
  return shapeValidationMemo[id];
};

export function* shapeValidation(shape, queryString) {
  const { validateShapeSuccess, validateShapeFail } = actions;
  const valueInMemo = yield call(getFromMemo, queryString);
  if (valueInMemo) {
    return yield put(validateShapeSuccess(valueInMemo));
  }
  try {
    const shapeValidationResponse = yield fetch(
      `/gasket-configurator/api/${shape}Validation?${queryString}`,
    );
    if (shapeValidationResponse.status !== 200) {
      // @ts-ignore
      yield call(setInMemo, queryString, null);
      return yield put(validateShapeFail('failed to fetch'));
    } else {
      const validationDTO: ShapeValidationResponse =
        yield shapeValidationResponse.json();
      const validationResult = transformValidationDTO(validationDTO);
      yield call(setInMemo, queryString, validationResult);

      return yield put(validateShapeSuccess(validationResult));
    }
  } catch (e) {
    yield put(validateShapeFail(e));
  }
}

export function* fetchRingValidation(
  action: PayloadAction<RingValidationInput>,
) {
  const {
    payload: {
      outerDiameter,
      innerDiameter,
      diameterOfBoltHoles,
      pitchCircle,
      numberOfBoltHoles,
    },
  } = action;

  const singleItem: SingleItemState = yield select(selectSingleItem);
  const validationPayload: RingValidationPayload = {
    shape: 'ring',
    sheetLength: singleItem?.data?.length
      ? singleItem.data.length.toString()
      : '',
    sheetWidth: singleItem?.data?.width ? singleItem.data.width.toString() : '',
    diameter: outerDiameter,
    internalDiameter: innerDiameter,
    ...(numberOfBoltHoles !== '0'
      ? { diameterBoltholes: diameterOfBoltHoles }
      : {}),
    ...(pitchCircle !== '0' ? { pitchCircle: pitchCircle } : {}),
    numberOfHoles: numberOfBoltHoles,
  };

  const querystring = qs.stringify(validationPayload);

  yield call(shapeValidation, 'ring', querystring);
}

export function* fetchDiscValidation(
  action: PayloadAction<DiscValidationInput>,
) {
  const {
    payload: {
      outerDiameter,
      numberOfBoltHoles,
      diameterOfBoltHoles,
      pitchCircle,
    },
  } = action;
  const singleItem: SingleItemState = yield select(selectSingleItem);

  const validationPayload: DiscValidationPayload = {
    shape: 'disc',
    sheetWidth: singleItem?.data?.width.toString() || '',
    sheetLength: singleItem?.data?.length.toString() || '',
    diameter: outerDiameter,
    ...(numberOfBoltHoles !== '0'
      ? { diameterBoltholes: diameterOfBoltHoles }
      : {}),
    numberOfHoles: numberOfBoltHoles,
    ...(pitchCircle !== '0' || typeof pitchCircle !== 'undefined'
      ? { pitchCircle }
      : {}),
  };

  const queryString = qs.stringify(validationPayload);
  yield call(shapeValidation, 'disc', queryString);
}

export function* fetchFrameValidation(
  action: PayloadAction<FrameValidationInput>,
) {
  const {
    payload: {
      outsideLength,
      outsideWidth,
      insideLength,
      insideWidth,
      outsideRadius,
      insideRadius,
    },
  } = action;

  const singleItem: SingleItemState = yield select(selectSingleItem);
  const validationPayload: FrameValidationPayload = {
    shape: 'frame',
    outsideWidth,
    outsideLength,
    insideLength,
    insideWidth,
    outsideRadius,
    insideRadius,
    sheetLength: singleItem?.data?.length
      ? singleItem.data.length.toString()
      : '',
    sheetWidth: singleItem?.data?.width ? singleItem.data.width.toString() : '',
  };

  const queryString = qs.stringify(validationPayload);
  yield call(shapeValidation, 'frame', queryString);
}

export function* fetchStripValidation(
  action: PayloadAction<StripValidationInput>,
) {
  const {
    payload: { length, width },
  } = action;
  const singleItem = yield select(selectSingleItem);

  const validationPayload: StripValidationPayload = {
    shape: 'strip',
    length,
    width,
    sheetLength: singleItem?.data?.length,
    sheetWidth: singleItem?.data?.width,
  };
  const queryString = qs.stringify(validationPayload);
  yield call(shapeValidation, 'strip', queryString);
}

export function* frameValidationSaga() {
  yield debounce(500, actions.validateFrame.type, fetchFrameValidation);
}

export function* stripValidationSaga() {
  yield debounce(500, actions.validateStrip.type, fetchStripValidation);
}

export function* ringValidationSaga() {
  yield debounce(500, actions.validateRing.type, fetchRingValidation);
}

export function* discValidationSaga() {
  yield debounce(500, actions.validateDisc.type, fetchDiscValidation);
}

export function* shapeValidationSaga() {
  yield all([
    call(ringValidationSaga),
    call(discValidationSaga),
    call(frameValidationSaga),
    call(stripValidationSaga),
  ]);
}
